import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, Phone, MoreVertical, Image, Smile, AlertTriangle } from 'lucide-react';
import { chatService, ChatMessage } from '../../services/chat.service';
import { auth } from '../../config/firebase.config';
import { notificationService } from '../../services/notification.service';

interface Message {
  id: string;
  sender: 'rider' | 'customer';
  text: string;
  time: string;
  status?: 'sent' | 'delivered' | 'read';
}

interface CustomerChatProps {
  customerName: string;
  customerPhone: string;
  jobId: string;
  deliveryAddress: string;
  riderName: string; // Add rider name for sending messages
  onBack: () => void;
  onCall: () => void;
}

const quickReplies = [
  "I'm on my way 🏍️",
  "Arriving in 5 minutes ⏱️",
  "I'm at the location 📍",
  "Please come outside",
  "Need help finding you",
  "Package picked up ✅"
];

export function CustomerChat({
  customerName,
  customerPhone,
  jobId,
  deliveryAddress,
  riderName,
  onBack,
  onCall
}: CustomerChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 🔥 Real-time Firebase chat listener
  useEffect(() => {
    if (!jobId) return;

    console.log('🔥 Subscribing to chat for job:', jobId);

    // Initialize chat when component mounts
    const currentUser = auth.currentUser;
    if (currentUser) {
      chatService.initializeChat(
        jobId,
        { id: currentUser.uid, name: riderName, phone: '' },
        { name: customerName, phone: customerPhone }
      );
    }

    // Subscribe to messages
    const unsubscribe = chatService.subscribeToMessages(jobId, (firebaseMessages) => {
      const formattedMessages: Message[] = firebaseMessages.map(msg => ({
        id: msg.id,
        sender: msg.sender,
        text: msg.text,
        time: msg.timestamp.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }),
        status: msg.status
      }));
      
      setMessages(formattedMessages);
      console.log('💬 Received messages:', formattedMessages.length);
    });

    return () => {
      console.log('🔌 Unsubscribing from chat');
      unsubscribe();
    };
  }, [jobId, customerName, customerPhone, riderName]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Detect new customer messages and trigger a notification
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage.sender === 'customer' &&
        messages.length > prevMessageCountRef.current
      ) {
        notificationService.trigger(
          'message',
          `💬 New message from Customer`,
          lastMessage.text.length > 60
            ? lastMessage.text.slice(0, 60) + '…'
            : lastMessage.text,
          { jobId }
        );
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 2000);
      }
      prevMessageCountRef.current = messages.length;
    }
  }, [messages, jobId]);

  // 🔥 Send message to Firebase
  const handleSend = async () => {
    if (messageText.trim() && !isSending) {
      setIsSending(true);
      try {
        await chatService.sendMessage(jobId, messageText, riderName);
        setMessageText('');
        setShowQuickReplies(false);
        console.log('✅ Message sent successfully');
      } catch (error) {
        console.error('❌ Error sending message:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setIsSending(false);
      }
    }
  };

  // 🔥 Send quick reply to Firebase
  const handleQuickReply = async (reply: string) => {
    if (!isSending) {
      setIsSending(true);
      try {
        await chatService.sendMessage(jobId, reply, riderName);
        setShowQuickReplies(false);
        console.log('✅ Quick reply sent successfully');
      } catch (error) {
        console.error('❌ Error sending quick reply:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setIsSending(false);
      }
    }
  };

  const formatTime = (timeString: string) => {
    // Simple time formatting
    return timeString;
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[#111111]" />
            </button>
            
            {/* Customer Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E0001B] to-[#C00017] flex items-center justify-center text-white font-semibold">
              {customerName.charAt(0)}
            </div>
            
            <div>
              <p className="text-[16px] text-[#111111] font-medium">{customerName}</p>
              <p className="text-[12px] text-[#6B6B6B]">Customer</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onCall}
              className="w-10 h-10 rounded-full bg-[#00B277] flex items-center justify-center hover:bg-[#009966] transition-colors shadow-sm"
            >
              <Phone className="w-5 h-5 text-white" />
            </button>
            <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
              <MoreVertical className="w-5 h-5 text-[#111111]" />
            </button>
          </div>
        </div>

        {/* Job Info */}
        <div className="bg-gradient-to-r from-[#E0001B]/10 to-[#FF1A35]/10 border border-[#E0001B]/20 rounded-lg p-3 mt-2">
          <div className="flex items-start gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-[#E0001B] flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#E0001B] font-semibold mb-0.5">🔴 ACTIVE DELIVERY CHAT</p>
              <p className="text-[11px] text-[#6B6B6B]">This chat is for delivery <b>#{jobId}</b> only</p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#E0001B]/10">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#6B6B6B] mb-0.5">DELIVERY TO</p>
              <p className="text-[12px] text-[#111111] truncate font-medium">{deliveryAddress}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50">
        {/* WARNING BANNER */}
        <div className="bg-red-50 border border-red-200 rounded-[12px] p-3 mb-4 sticky top-0 z-10">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-semibold text-red-900 mb-0.5">⚠️ POLICY WARNING</p>
              <p className="text-[10px] text-red-800 leading-tight">
                <b>DO NOT</b> share phone, WhatsApp, email or personal info. Violations = penalties & termination.
              </p>
            </div>
          </div>
        </div>

        {/* Date Separator */}
        <div className="flex items-center justify-center mb-4">
          <div className="bg-gray-200 rounded-full px-3 py-1">
            <span className="text-[11px] text-[#6B6B6B]">Today</span>
          </div>
        </div>

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex mb-3 ${message.sender === 'rider' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-[16px] px-4 py-2.5 ${
                message.sender === 'rider'
                  ? 'bg-[#E0001B] text-white rounded-br-[4px]'
                  : 'bg-white text-[#111111] rounded-bl-[4px] shadow-sm'
              }`}
            >
              <p className="text-[14px] leading-relaxed">{message.text}</p>
              <div className={`flex items-center gap-1 mt-1 ${message.sender === 'rider' ? 'justify-end' : 'justify-start'}`}>
                <span className={`text-[11px] ${message.sender === 'rider' ? 'text-white/80' : 'text-[#6B6B6B]'}`}>
                  {formatTime(message.time)}
                </span>
                {message.sender === 'rider' && message.status === 'read' && (
                  <div className="flex gap-[1px]">
                    <div className="w-3 h-3 rounded-full bg-white/30 flex items-center justify-center">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex justify-start mb-3">
            <div className="bg-white rounded-[16px] rounded-bl-[4px] px-4 py-3 shadow-sm">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies */}
      {showQuickReplies && (
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <p className="text-[12px] text-[#6B6B6B] mb-2">Quick Replies</p>
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => handleQuickReply(reply)}
                className="flex-shrink-0 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-[13px] text-[#111111] transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-end gap-2">
          <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0">
            <Image className="w-5 h-5 text-[#6B6B6B]" />
          </button>

          <div className="flex-1 bg-gray-100 rounded-[20px] px-4 py-2.5 flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 bg-transparent text-[14px] text-[#111111] placeholder:text-[#6B6B6B] outline-none"
            />
            <button className="flex-shrink-0">
              <Smile className="w-5 h-5 text-[#6B6B6B]" />
            </button>
          </div>

          <button
            onClick={handleSend}
            disabled={!messageText.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
              messageText.trim()
                ? 'bg-[#E0001B] hover:bg-[#C00017] shadow-sm'
                : 'bg-gray-200'
            }`}
          >
            <Send className={`w-5 h-5 ${messageText.trim() ? 'text-white' : 'text-gray-400'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}